import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { awards, type Award } from "../../../engine/awards";
import { benchPoints, bountiesOn, cursesNow, curseName, pickOutcome, pickTally, twistNow, type TwistNow } from "../../../engine/bench";
import { insights, type Insight } from "../../../engine/insights";
import { dayPlan } from "../../../engine/plan";
import { gamesDone, windDownNow } from "../../../engine/winddown";
import { funBoard, mainBoard, podium, type FunRow, type MainRow } from "../../../engine/ranking";
import { eventById } from "../../../engine/scoring";
import { NOTICE_MS } from "../../../engine/state";
import { BREAK_OVER, breakNow, countdown, waitingFor, wallNotice } from "../../../shared/day";
import { allScoresIn, availablePlayers, currentRound, displayName, lostLast, owedScores } from "../../../engine/selectors";
import type { CourtGame, Curse, GameRecord, Heckle, Pair, Phase, Pick, Player, PlayerId, RevealStep, Round, TournamentState } from "../../../engine/types";
import { useTournament, type Screen } from "../lib/useTournament";
import { STATUS_WORDS } from "../lib/transport";
import { anthem, drumRoll, fanfare, flourish, hush, soundRemembered, sting, unlock, whistle } from "../lib/buzzer";
import { STEP_ROLL_MS } from "../lib/reveal";
import { toggleFullscreen, useFullscreen, useWakeLock } from "../lib/screenTools";
import { Face } from "../components/Face";
import { FitText } from "../components/FitText";
import { Mascot, MascotSays } from "../components/Mascot";
import { useLook } from "../lib/look";
import { MASCOT, REVIEW, isFavourite, lineFor, poseFor, reviewedCourt, roastFor, type MascotMoment, type MascotPose } from "../lib/mascot";
import { filterOf, loadTuning } from "./projector/calibration";
import { demoCourts, demoCourtsFromLocation } from "./projector/demoCourts";
import { demoDay, demoDayFromLocation } from "./projector/demoDay";
import { FitLines } from "./projector/FitLines";
import { columnsFor, isHere, JoinWall } from "./projector/JoinWall";
import { Notice } from "./projector/Notice";
import { Qr } from "./projector/qr";
import { Slides, demoSlideFromLocation } from "./projector/Slides";
import { describeWall, type WallView } from "./projector/describeWall";
import { postWall, snapJpeg } from "../lib/feedback";
import { SLIDE_TITLES } from "../lib/slides";
import "@fontsource/barlow-condensed/latin-600.css";
import "../far.css";
import "./Projector.css";

const STATUS_LABEL = STATUS_WORDS;
const ROWS_PER_COLUMN = 8;
const ANNOUNCE_MS = 6000;
/**
 * The podium, lowest place first (decision 43): 3rd at once, 2nd five seconds later, the winner
 * after a longer roll. Each place but the first has a drum roll leading into it.
 */
const PLACE_AT_MS = [0, 5000, 12_000];
const ROLL_MS = [0, 3000, 5000];
/**
 * The reveal stepped from the owner's phone (decision 53): the drum roll before each place, by
 * place (1st, 2nd, 3rd), and how fresh a step must be for the wall to play it (a wall reloaded
 * mid-reveal shows where it is, silently).
 */
const STEP_FRESH_MS = 8000;
const RESULTS_MS = 6000;
const INSIGHT_MS = 4000;
/** How long the host calls time before it turns to the results. */
const TIME_MS = 2500;
const LAST_MINUTE_MS = 60_000;
/** The clock jumps on each of the last seconds. */
const FINAL_SECONDS = 10;
/** How long a heckle stays up after it arrives. */
const HECKLE_MS = 20_000;
/** A twist's card when it starts, and "Back to normal" in its band when it ends. */
const TWIST_CARD_MS = 4000;
const TWIST_OVER_MS = 3000;
/** How long the host says who threw a curse. */
const CURSE_SAY_MS = 8000;
/** One award at a time. */
const AWARD_MS = 6000;
/** The fake review on the results card: "Checking" for three seconds, then "Stands" for two. */
const CHECKING_MS = 3000;
const STANDS_MS = 2000;

/**
 * Test hook for the overlays: ?demo=reveal|pause|event|owed|idle|join|results|review fakes that
 * state on this screen only. reveal waits for the first score so the insights reel has something
 * to show; owed takes the score off court 2 of the round before the current one; idle (or join)
 * is the day before round 1, the join wall. results holds the results card of the first round
 * with every score in (or of &round=N); review does the same and replays the fake review on it.
 * twist starts a twist on the round being played a few seconds after the page opens, and again
 * after each one ends; curse does the same with a curse on court 1's top pair; picks has everyone
 * resting pick every court; awards hands out the awards as they stand. notice puts a message on
 * the wall (&text=... for other words), for its 20 seconds and again a few seconds after.
 * courts3 and courts2 are the day with that many courts in use; with &results=1 they hold the
 * results card. lunch, lunchover, held and waiting are in projector/demoDay.ts. slides (with
 * &slide=N, 1-based) shows that slide of the talk before round 1, with no change to the day.
 * host puts the host in all eight moods across the wall, moving, to check the drawing (decision 44).
 * few is between rounds with three here and the rest on a break: too few to draw a round.
 */
const DEMOS = ["reveal", "pause", "event", "owed", "idle", "results", "review", "twist", "curse", "picks", "awards", "notice", "courts3", "courts2", "slides", "host", "few"] as const;
type Demo = (typeof DEMOS)[number] | null;
function demoFromLocation(): Demo {
  const d = new URLSearchParams(location.search).get("demo");
  if (d === "join") return "idle";
  return DEMOS.find((x) => x === d) ?? null;
}
/** How often ?demo=review replays the review. */
const DEMO_REVIEW_LOOP_MS = 9000;
/** ?demo=twist|curse: the wait before the first one, and between one and the next. */
const DEMO_BENCH_WAIT_MS = 3000;
const DEMO_BENCH_GAP_MS = 30_000;
/** ?demo=notice: the wait between one showing of the message and the next. */
const DEMO_NOTICE_GAP_MS = 4000;

/** The round being played, or null. */
function playingRound(s: TournamentState): Round | null {
  return (s.clock.phase === "playing" && s.rounds.find((r) => r.no === s.clock.roundNo)) || null;
}

/** ?demo=twist|curse: a window of `ms` that opens a few seconds after `openedAt` and again after each gap. */
function demoWindow(openedAt: number, now: number, ms: number): { startsAt: number; endsAt: number } | null {
  const since = now - openedAt - DEMO_BENCH_WAIT_MS;
  if (since < 0) return null;
  const startsAt = now - (since % (ms + DEMO_BENCH_GAP_MS));
  return { startsAt, endsAt: startsAt + ms };
}

/** ?demo=twist: the first handicap as the twist of the round being played. */
function demoTwist(s: TournamentState, openedAt: number, now: number): TournamentState {
  const round = playingRound(s);
  const type = s.config.eventTypes.find((e) => e.kind === "handicap");
  const at = demoWindow(openedAt, now, s.config.twistMs);
  if (!round) return s;
  const twist = type && at ? { eventId: type.id, ...at } : null;
  return { ...s, rounds: s.rounds.map((r) => (r === round ? { ...r, twist } : r)) };
}

/** ?demo=curse: somebody resting curses court 1's top pair. */
function demoCurse(s: TournamentState, openedAt: number, now: number): TournamentState {
  const round = playingRound(s);
  const game = round?.games[0];
  const by = round?.lineup.sittingOut[0];
  const at = demoWindow(openedAt, now, s.config.bench.curseMs);
  if (!round || !game || !by) return s;
  const curses = s.bench.curses.filter((c) => c.roundNo !== round.no);
  if (at) curses.push({ by, roundNo: round.no, court: game.court, side: "a", ...at });
  return { ...s, bench: { ...s.bench, curses } };
}

/** ?demo=notice: a message that goes up as the page opens, and again after each one has gone. */
function demoNotice(s: TournamentState, openedAt: number, now: number): TournamentState {
  const text = new URLSearchParams(location.search).get("text") || "Food is ready";
  const loop = NOTICE_MS + DEMO_NOTICE_GAP_MS;
  const at = openedAt + Math.floor(Math.max(0, now - openedAt) / loop) * loop;
  return { ...s, notice: { text, at, by: "demo" } };
}

/** ?demo=few: between rounds, three here and everyone else on a break, so nothing is drawn. */
function demoFew(s: TournamentState): TournamentState {
  const keep = new Set(availablePlayers(s).slice(0, 3));
  const players = Object.fromEntries(
    Object.entries(s.players).map(([id, p]) => [id, p.status === "in" && !keep.has(id) ? { ...p, status: "break" as const } : p]),
  );
  return { ...s, players, draft: null, clock: { phase: "idle", roundNo: null, phaseEndsAt: null, pausedAt: null } };
}

/** ?demo=picks: everyone resting picks every court of the round being played. */
function demoPicks(s: TournamentState): TournamentState {
  const round = playingRound(s);
  if (!round) return s;
  const picks: Pick[] = s.bench.picks.filter((p) => p.roundNo !== round.no);
  round.lineup.sittingOut.forEach((by, i) => {
    for (const g of round.games) picks.push({ by, roundNo: round.no, court: g.court, pair: (i + g.court) % 3 ? g.a : g.b });
  });
  return { ...s, bench: { ...s.bench, picks } };
}

/** A player's picture for a Face, from the tournament hook; null until they have one. */
const Faces = createContext<(p: Player | undefined) => string | null>(() => null);

const CLOCK = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/** Which of the four court colours a court wears. */
function colourNo(court: number): number {
  return ((court - 1) % 4) + 1;
}

function courtColour(court: number): CSSProperties {
  return { background: `var(--bd-court-${colourNo(court)})` };
}

function nameOf(s: TournamentState, id: PlayerId): string {
  return displayName(s.players[id], "wall");
}

/** One player on a block of colour: the face (stamped if they lost last, crowned if the favourite), then the name fitted to its line. */
function Who({ s, id }: { s: TournamentState; id: PlayerId }) {
  const faceOf = useContext(Faces);
  const name = nameOf(s, id);
  return (
    <div className="who">
      <Face name={name} src={faceOf(s.players[id])} stamped={lostLast(s, id)} crowned={isFavourite(id)} />
      <FitText className="who-name wall-display">{name}</FitText>
    </div>
  );
}

/**
 * The host and its line for a moment. The round number picks the line, so every screen agrees.
 * `say` is a line of its own for the moment (a roast, the review, the bets).
 */
function Host({ moment, seed, say }: { moment: MascotMoment; seed: number; say?: { line: string; pose?: MascotPose } | null }) {
  return <MascotSays className="proj-host" pose={say?.pose ?? poseFor(moment)} line={say?.line ?? lineFor(moment, seed)} />;
}

/** A starburst sticker for a shout. */
function Burst({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className={`burst ${className}`}>
      <span className="burst-in">{children}</span>
    </span>
  );
}

/** A heckle in the host's corner: the bubble wears the colour of the court it is aimed at, its tail points at whoever sent it. */
function HeckleSays({ s, h }: { s: TournamentState; h: Heckle }) {
  const faceOf = useContext(Faces);
  const sender = s.players[h.by];
  const name = displayName(sender, "wall");
  return (
    <div className="proj-heckle">
      <p className={h.court === null ? "heckle-bubble hall" : "heckle-bubble"} style={h.court === null ? undefined : courtColour(h.court)}>
        {h.text}
      </p>
      <div className="heckle-who">
        <Face name={name} src={faceOf(sender)} className="on-ground" />
        <FitText className="heckle-name wall-display">{name}</FitText>
      </div>
    </div>
  );
}

/**
 * How a round's picks went, for the results card: "Right picks: 9 of 14" and "On a run: Big
 * Serve, 4" (whoever is on the longest run of right picks, from three in a row). Two lines of
 * the header, so neither has to shrink.
 */
function picksSettled(s: TournamentState, roundNo: number): { right: string | null; run: string | null } {
  const settled = s.bench.picks.filter((p) => p.roundNo === roundNo).map((p) => pickOutcome(s, p)).filter((o) => o !== null);
  const right = settled.length ? `Right picks: ${settled.filter(Boolean).length} of ${settled.length}` : null;
  const bench = [...benchPoints(s)];
  const longest = Math.max(0, ...bench.map(([, r]) => r.streak));
  if (longest < 3) return { right, run: null };
  const on = bench.filter(([, r]) => r.streak === longest).map(([id]) => nameOf(s, id));
  const who = on.length > 2 ? `${on[0]} and ${on.length - 1} more` : on.join(" and ");
  return { right, run: `On a run: ${who}, ${longest}` };
}

/** The court a forced review (?demo=review) looks at: the closest scored game. */
function closestCourt(round: Round): number | null {
  const games = round.games.filter((g) => g.score && !g.voided);
  const gap = (g: GameRecord): number => Math.abs((g.score?.a ?? 0) - (g.score?.b ?? 0));
  return [...games].sort((x, y) => gap(x) - gap(y))[0]?.court ?? null;
}

interface Review {
  court: number;
  stage: "checking" | "stands";
}

function clockText(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

/** "Every court", "Court 2", "Courts 1 and 3", "Courts 1, 2 and 4". */
function courtsText(courts: number[], total: number): string {
  if (courts.length === total && total > 1) return "Every court";
  if (courts.length === 1) return `Court ${courts[0]}`;
  return `Courts ${courts.slice(0, -1).join(", ")} and ${courts[courts.length - 1]}`;
}

interface Announcement {
  key: number;
  /** `note` stands in for the courts (a twist: "Whole hall. 90 seconds."). */
  items: { name: string; courts: number[]; note?: string }[];
  total: number;
  /** Above the names; "This round" unless given. */
  label?: string;
  /** How long it stays up; ANNOUNCE_MS unless given. */
  ms?: number;
}

function useTick(ms: number): void {
  const [, set] = useState(0);
  useEffect(() => {
    const id = setInterval(() => set((n) => n + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
}

export function ProjectorView({ screen }: { screen: Screen }) {
  const t = useTournament(screen);
  useWakeLock();
  useTick(200);
  const now = t.now();
  const demo = useRef<Demo>(demoFromLocation()).current;
  const frozenAt = useRef(now).current;

  const [tuning] = useState(loadTuning);
  useLook(t.state?.config.look);

  const [soundOn, setSoundOn] = useState(soundRemembered);
  const enableSound = () => {
    unlock();
    setSoundOn(true);
  };

  let s = t.state;
  if (s && demo === "pause") {
    // &text=... is a message sent during the pause
    const text = new URLSearchParams(location.search).get("text");
    s = { ...s, pausedAll: true, clock: { ...s.clock, pausedAt: s.clock.pausedAt ?? frozenAt }, notice: text ? { text, at: frozenAt, by: "demo" } : s.notice };
  }
  if (s && demo === "reveal" && s.rounds.some((r) => r.games.some((g) => g.score))) s = { ...s, reveal: { main: true, fun: true, awards: false } };
  if (s && demo === "awards" && s.rounds.some((r) => r.games.some((g) => g.score))) s = { ...s, reveal: { main: false, fun: false, awards: true } };
  if (s && demo === "twist") s = demoTwist(s, frozenAt, now);
  if (s && demo === "curse") s = demoCurse(s, frozenAt, now);
  if (s && demo === "picks") s = demoPicks(s);
  if (s && demo === "notice") s = demoNotice(s, frozenAt, now);
  const fewer = useRef(demoCourtsFromLocation()).current;
  if (s && fewer) s = demoCourts(s, fewer);
  const pretend = useRef(demoDayFromLocation()).current;
  if (s && pretend) s = demoDay(s, pretend, frozenAt);
  if (s && demo === "few") s = demoFew(s);
  if (s && demo === "idle") s ={ ...s, rounds: [], clock: { phase: "idle", roundNo: null, phaseEndsAt: null, pausedAt: null } };
  if (s && demo === "owed" && s.rounds.length >= 2) {
    const i = s.rounds.length - 2;
    s = {
      ...s,
      rounds: s.rounds.map((r, j) => (j === i ? { ...r, games: r.games.map((g) => (g.court === 2 ? { ...g, score: null, voided: false } : g)) } : r)),
    };
  }

  // The buzzer: three flashes and the whistle when the game ends, one flash and one short blast
  // when a round starts. Nothing sounds over the pause screen.
  // The end of a game also brings up that round's results for six seconds before the board.
  const [flash, setFlash] = useState<{ key: number; times: 1 | 3 } | null>(null);
  const [results, setResults] = useState<{ roundNo: number; since: number } | null>(null);
  const prevPhase = useRef<Phase | null>(null);
  const phase = s?.clock.phase ?? null;
  useEffect(() => {
    if (!phase) return;
    const prev = prevPhase.current;
    prevPhase.current = phase;
    if (!prev || prev === phase) return;
    const quiet = !!s?.pausedAll;
    if (prev === "playing" && phase === "changeover") {
      setFlash({ key: performance.now(), times: 3 });
      if (!quiet) whistle();
      const roundNo = s?.clock.roundNo ?? (s ? currentRound(s)?.no : null);
      if (roundNo) setResults({ roundNo, since: t.now() });
    } else if (phase === "playing") {
      setFlash({ key: performance.now(), times: 1 });
      if (!quiet) whistle(1);
    }
  }, [phase]);

  // Event announcements: any event that lands on the running round, shown for six seconds.
  const [announce, setAnnounce] = useState<Announcement | null>(null);
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!s) return;
    const round = currentRound(s);
    const live = round && s.clock.roundNo === round.no ? round : null;
    const keys = live ? live.games.filter((g) => g.eventId).map((g) => `${live.no}|${g.court}|${g.eventId}`) : [];
    if (!seen.current) {
      // What is already on screen when the page opens is not news.
      seen.current = new Set(keys);
      if (demo !== "event") return;
      const types = s.config.eventTypes;
      const all = live?.games.map((g) => g.court) ?? [1, 2, 3, 4];
      setAnnounce({
        key: -1,
        total: all.length,
        items: types[0] ? [{ name: types[0].name, courts: all }] : [],
      });
      return;
    }
    const known = seen.current;
    const fresh = keys.filter((k) => !known.has(k));
    if (!fresh.length || !live) return;
    const byEvent = new Map<string, number[]>();
    for (const k of fresh) {
      known.add(k);
      const [, court, id] = k.split("|") as [string, string, string];
      byEvent.set(id, [...(byEvent.get(id) ?? []), Number(court)]);
    }
    const items = [...byEvent].map(([id, courts]) => ({
      name: eventById(s.config, id)?.name ?? id,
      courts: courts.sort((x, y) => x - y),
    }));
    setAnnounce({ key: performance.now(), items, total: live.games.length });
  }, [s, demo]);
  useEffect(() => {
    if (!announce || announce.key < 0) return;
    const id = setTimeout(() => setAnnounce(null), announce.ms ?? ANNOUNCE_MS);
    return () => clearTimeout(id);
  }, [announce]);

  // A twist: its card and the whistle when it starts; the whistle and "Back to normal" in its
  // band when it ends inside the round. One that is already running when the page opens is not news.
  const twistRaw = s ? twistNow(s, now) : null;
  const twist = twistRaw?.on ? twistRaw : null;
  const twistKey = twist && s ? `${s.clock.roundNo}|${twist.eventId}` : null;
  const twistWas = useRef<string | null | undefined>(undefined);
  const [twistOver, setTwistOver] = useState<number | null>(null);
  const loaded = !!s;
  useEffect(() => {
    if (!loaded) return;
    const was = twistWas.current;
    twistWas.current = twistKey;
    // a results card held by ?demo=results|review is not interrupted by the day running under it
    if (was === undefined || was === twistKey || held.current) return;
    const quiet = !!s?.pausedAll;
    if (twist) {
      const seconds = Math.round((twist.endsAt - twist.startsAt) / 1000);
      setTwistOver(null);
      setAnnounce({ key: performance.now(), label: "Twist", items: [{ name: twist.name, courts: [], note: `Whole hall. ${seconds} seconds.` }], total: 0, ms: TWIST_CARD_MS });
      if (!quiet) whistle();
    } else {
      // stopped while its card was still up: the card goes with it
      setAnnounce((a) => (a?.ms === TWIST_CARD_MS ? null : a));
      if (phase !== "playing") return;
      setTwistOver(performance.now());
      if (!quiet) whistle();
    }
  }, [loaded, twistKey]);
  useEffect(() => {
    if (twistOver === null) return;
    const id = setTimeout(() => setTwistOver(null), TWIST_OVER_MS);
    return () => clearTimeout(id);
  }, [twistOver]);

  // the owner's stepped reveal (decision 53) decides on its own; without it, the old switches
  const stepped = s?.revealStep ?? null;
  const podiums = !!s && (stepped ? stepped.step > stepped.of : s.reveal.main || s.reveal.fun);
  const revealing = podiums || !!(stepped ? stepped.step >= 1 : s?.reveal.awards);
  // a fresh step forward plays its music; Back, an undo to an earlier step, or a reloaded wall do not
  // ("unset": this page has not seen the day yet, so a step already up when it loaded is not news)
  const lastStep = useRef<{ step: number | null | "unset"; forward: boolean }>({ step: "unset", forward: false });
  if (s) {
    const cur = stepped?.step ?? null;
    const was = lastStep.current.step;
    if (cur !== was) lastStep.current = { step: cur, forward: was !== "unset" && cur !== null && (was === null || cur > was) };
  }
  const freshStep = !!stepped && now - stepped.at < STEP_FRESH_MS && lastStep.current.forward;
  const late = s ? lateScores(s) : [];
  const round = s ? currentRound(s) : null;
  const seed = round?.no ?? 0;

  // The results card: six seconds after the whistle, or held by ?demo=results|review.
  const held = useRef<{ roundNo: number; at: number } | null>(null);
  const holdResults = demo === "results" || demo === "review" || (!!fewer && new URLSearchParams(location.search).get("results") === "1");
  if (s && holdResults && !held.current) {
    const want = Number(new URLSearchParams(location.search).get("round")) || 0;
    const done = s.rounds.find((r) => (!want || r.no === want) && allScoresIn(r) && r.games.some((g) => g.score && !g.voided));
    if (done) held.current = { roundNo: done.no, at: Date.now() };
  }
  let card: { round: Round; elapsed: number } | null = null;
  if (s && held.current) {
    const r = s.rounds.find((x) => x.no === held.current?.roundNo);
    const since = Date.now() - held.current.at;
    if (r) card = { round: r, elapsed: demo === "review" ? since % DEMO_REVIEW_LOOP_MS : since };
  } else if (s && results && s.clock.phase === "changeover" && now < results.since + RESULTS_MS) {
    const r = s.rounds.find((x) => x.no === results.roundNo);
    if (r) card = { round: r, elapsed: now - results.since };
  }
  let review: Review | null = null;
  if (s && card && card.elapsed < CHECKING_MS + STANDS_MS) {
    const court = reviewedCourt(s, card.round) ?? (demo === "review" ? closestCourt(card.round) : null);
    if (court !== null) review = { court, stage: card.elapsed < CHECKING_MS ? "checking" : "stands" };
  }

  // What the host in the header is talking about.
  let moment: MascotMoment = "idle";
  let say: { line: string; pose?: MascotPose } | null = null;
  if (s) {
    const { phaseEndsAt, pausedAt } = s.clock;
    const left = phaseEndsAt === null ? Infinity : phaseEndsAt - (pausedAt ?? now);
    if (card) {
      // the review first, then time is called, then the verdict on the round
      const roast = roastFor(s, card.round);
      if (review) say = review.stage === "checking" ? { line: REVIEW.checking, pose: "whistle" } : { line: REVIEW.stands, pose: "smug" };
      else if (card.elapsed < TIME_MS) moment = "time";
      else {
        moment = "results";
        if (roast) say = { line: roast };
      }
    } else if (s.clock.phase === "playing") {
      moment = left <= 0 ? "time" : left <= LAST_MINUTE_MS ? "lastMinute" : late.length ? "owed" : "roundStart";
    } else if (late.length) moment = "owed";
    else if (s.rounds.length) {
      moment = "results";
      const roast = s.clock.phase === "changeover" && round ? roastFor(s, round) : null;
      if (roast) say = { line: roast };
    }
  }
  // A fresh heckle takes the host's corner for twenty seconds, unless the host is calling time.
  const heckle = s && !s.bench.muted ? s.bench.heckles[s.bench.heckles.length - 1] : undefined;
  const heckling = !!heckle && !card && moment !== "time" && moment !== "lastMinute" && now - heckle.at < HECKLE_MS;
  // A curse just thrown: the host says who did it, ahead of anything else in its corner.
  const curses = s && !card ? cursesNow(s, now) : [];
  const thrown = curses.filter((c) => c.endsAt - c.startsAt - c.leftMs < CURSE_SAY_MS).pop();
  const cursedGame = thrown && round?.games.find((g) => g.court === thrown.court);
  if (s && thrown && cursedGame) {
    const pair = cursedGame[thrown.side];
    const stays = thrown.stepsOff ? pair.find((id) => id !== thrown.stepsOff) : undefined;
    const what = stays ? `${nameOf(s, stays)} plays alone` : curseName(thrown).toLowerCase();
    say = { line: `${nameOf(s, thrown.by)} cursed ${nameOf(s, pair[0])} & ${nameOf(s, pair[1])}: ${what}. Shameless. I approve.`, pose: "smug" };
  }
  const host =
    s && heckle && heckling && !thrown ? <HeckleSays key={heckle.seq} s={s} h={heckle} /> : <Host moment={moment} seed={seed} say={say} />;

  // The admin's message: up for twenty seconds from when it was sent. While everything is
  // paused it is a line of the pause screen instead, and stays until the pause ends.
  const notice = s ? wallNotice(s, now) : null;
  const pauseScreen = !!s?.pausedAll && !revealing;
  // while the message is up he stands on its block, so the header has no host of its own
  const topHost = notice && !pauseScreen ? null : host;

  // The slides before round 1 take the whole wall while they are on (only the owner's phone moves them).
  const demoSlide = useRef(demoSlideFromLocation()).current;
  const slide = demoSlide ?? (typeof s?.slides === "number" ? s.slides : null);
  const slidesOn = !!s && slide !== null;

  // before round 1 the wall counts who is in the hall, not who has joined from home
  const everyone = s ? Object.values(s.players).filter((p) => p.status !== "left") : [];
  const here = everyone.filter(isHere).length;

  // The owner's Note key (v26.10.027, temporary): what the wall shows now, in words, and its
  // picture, sent when the owner's phone asks. The award on the wall is told by Awards itself.
  const awardShown = useRef<WallView["award"]>(null);
  const showing = revealing && !podiums && !slidesOn ? awardShown.current : null;
  const view: WallView = {
    loaded: !!s,
    slide: slidesOn && slide !== null ? { n: slide + 1, of: SLIDE_TITLES.length, title: SLIDE_TITLES[slide] ?? "" } : null,
    beforeRound1: s && !s.rounds.length ? { here, total: everyone.length } : null,
    results: card ? card.round.no : null,
    playing: s && !card && s.clock.phase === "playing" && s.clock.roundNo !== null
      ? { round: s.clock.roundNo, leftMs: s.clock.phaseEndsAt === null ? 0 : s.clock.phaseEndsAt - (s.clock.pausedAt ?? now) }
      : null,
    between: s && s.rounds.length && !card && s.clock.phase !== "playing" ? { after: [...s.rounds].reverse().find((r) => allScoresIn(r))?.no ?? null } : null,
    twist: twist?.name ?? null,
    card: announce && !revealing ? { label: announce.label ?? "Mini game", names: announce.items.map((i) => i.name) } : null,
    pause: s && pauseScreen
      ? (() => {
          const on = breakNow(s, now);
          const { phaseEndsAt, pausedAt } = s.clock;
          return { label: on?.label ?? null, backAt: on?.backAt ?? null, over: !!on?.over, leftOnClockMs: !on && phaseEndsAt !== null && pausedAt !== null ? phaseEndsAt - pausedAt : null };
        })()
      : null,
    podium: s && podiums ? (stepped || s.reveal.main ? "main" : "fun") : null,
    award: showing,
    notice: notice?.text ?? null,
    demo: new URLSearchParams(location.search).get("demo"),
  };
  const viewNow = useRef(view);
  viewNow.current = view;
  const noteAsk = t.noteAsk;
  useEffect(() => {
    if (!noteAsk || screen.mock) return;
    const text = describeWall(viewNow.current);
    const wall = document.querySelector<HTMLElement>(".proj") ?? document.body;
    void snapJpeg(wall, 600_000).then((jpeg) => postWall(screen.tournament, screen.token, noteAsk.rid, text, jpeg));
  }, [noteAsk?.at]);

  return (
    <Faces.Provider value={t.faceOf}>
    <div className={late.length ? "far proj has-nag" : "far proj"} style={{ filter: filterOf(tuning) }} onClick={soundOn ? undefined : enableSound}>
      <Ops on={soundOn} status={t.status} />
      {!s ? (
        <div className="proj-wait wall-display">Connecting</div>
      ) : slidesOn ? (
        <Slides slide={slide} link={t.links?.players ?? null} />
      ) : !s.rounds.length ? (
        <>
          <Top title={`${here} of ${everyone.length} here`} sub="Before round 1" plan={planText(s, now)} middle={null} host={topHost} />
          <JoinWall s={s} link={revealing ? null : (t.links?.players ?? null)} faceOf={t.faceOf} />
        </>
      ) : (
        <>
          {card ? (
            <Results s={s} now={now} host={topHost} round={card.round} review={review} />
          ) : s.clock.phase === "playing" ? (
            <NowPlaying s={s} now={now} host={topHost} twist={twist} twistOver={twistOver} curses={curses} />
          ) : (
            <Leaderboard s={s} now={now} host={topHost} />
          )}
          <UpNext s={s} late={late} link={revealing ? null : (t.links?.players ?? null)} />
        </>
      )}
      {slidesOn ? null : (
        <>
          {announce && !revealing ? <EventCard a={announce} /> : null}
          {s && pauseScreen ? <Paused s={s} seed={seed} now={now} message={notice?.text ?? null} /> : null}
          {s && podiums ? <Reveal key={stepped ? "stepped" : s.reveal.main ? "main" : "fun"} s={s} now={now} seed={seed} stepped={stepped} fresh={freshStep} /> : null}
          {s && revealing && !podiums ? <Awards s={s} shown={awardShown} at={stepped ? stepped.step - 1 : undefined} of={stepped ? stepped.of : undefined} fresh={freshStep} /> : null}
          {flash && !revealing ? <div key={flash.key} className={`proj-flash times-${flash.times}`} onAnimationEnd={() => setFlash(null)} /> : null}
          {notice && !pauseScreen ? <Notice key={notice.at} text={notice.text} /> : null}
        </>
      )}
      {demo === "host" ? <HostSheet /> : null}
    </div>
    </Faces.Provider>
  );
}

/** ?demo=host: the host in every mood at once, moving, at about the size the wall shows it. */
const HOST_MOODS: MascotPose[] = ["calling", "whistle", "smug", "shock", "asleep", "nag", "crown", "point"];

function HostSheet() {
  return (
    <div className="proj-overlay proj-hosts">
      {HOST_MOODS.map((pose) => (
        <figure key={pose}>
          <Mascot pose={pose} />
          <figcaption className="wall-display">{pose}</figcaption>
        </figure>
      ))}
    </div>
  );
}

/**
 * For whoever is at the laptop, small in the corner: the connection, the sound badge until the
 * page has been clicked, and the Full screen button until it is.
 */
/** ?fps=1 on the projector's address: frames a second and the slowest frame of the last second, in the corner. */
const SHOW_FPS = new URLSearchParams(location.search).get("fps") === "1";

function Fps() {
  const [text, setText] = useState("");
  useEffect(() => {
    let frames = 0;
    let slowest = 0;
    let last = performance.now();
    let since = last;
    let id = requestAnimationFrame(function tick(t) {
      frames += 1;
      slowest = Math.max(slowest, t - last);
      last = t;
      if (t - since >= 1000) {
        setText(`${Math.round((frames * 1000) / (t - since))} fps, slowest ${Math.round(slowest)} ms`);
        frames = 0;
        slowest = 0;
        since = t;
      }
      id = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(id);
  }, []);
  return <span className="far-op">{text}</span>;
}

function Ops({ on, status }: { on: boolean; status: keyof typeof STATUS_LABEL }) {
  const full = useFullscreen();
  return (
    <div className="far-ops">
      {SHOW_FPS ? <Fps /> : null}
      {on ? null : <span className="far-op">Tap once for sound</span>}
      {full ? null : (
        <button type="button" className="far-op" onClick={() => void toggleFullscreen()}>
          Full screen
        </button>
      )}
      <span className={"status-pill " + status}>{STATUS_LABEL[status]}</span>
    </div>
  );
}

/** The header: what is on, the plan for the day, the countdown, the host. */
function Top({ title, sub, plan, middle, host }: { title: string; sub?: string | null; plan: string | null; middle: ReactNode; host: ReactNode }) {
  return (
    <header className="proj-top">
      <div className="proj-title">
        <h1>
          <FitText className="wall-display">{title}</FitText>
        </h1>
        {sub ? <FitText className="proj-sub">{sub}</FitText> : null}
        {plan ? <FitText className="proj-sub">{plan}</FitText> : null}
      </div>
      {middle ?? <div />}
      {host}
    </header>
  );
}

/** "Round 12 of about 22 · last round about 17:35", from the slot times and the last whistle. */
function planText(s: TournamentState, now: number): string | null {
  // a wind down knows exactly how many rounds are left (decision 42)
  const wind = windDownNow(s);
  if (wind) {
    // the board counts scored games: until the last is in, it is the rounds that are done
    if (gamesDone(s) && s.clock.phase !== "playing") return owedScores(s).length ? "The rounds are done · waiting for the last scores" : "The games are done";
    return `Round ${s.rounds.length} of ${s.rounds.length + wind.rounds.length} · the last rounds even out the games`;
  }
  if (s.config.lastWhistleAt === null) return null;
  const plan = dayPlan(s, now);
  const last = plan.lastRoundStartsAt !== null ? `last round about ${CLOCK.format(plan.lastRoundStartsAt)}` : "the last round";
  if (!plan.roundsSoFar) {
    if (!plan.totalExpected) return null;
    return `About ${plan.totalExpected} rounds · ${last}`;
  }
  if (!plan.roundsLeft) return `Round ${plan.roundsSoFar} · ${last}`;
  return `Round ${plan.roundsSoFar} of about ${plan.totalExpected} · ${last}`;
}

/**
 * Between rounds when an admin starts each one (decision 41): what the wall says in place of a
 * clock. Never a countdown and never "by itself": the next round is up to the admin.
 */
function nextRoundLine(s: TournamentState): string {
  if (gamesDone(s)) return "That was the last game";
  return "Next round starts when the courts are ready";
}

/** The clock for the changeover, or with rounds started by an admin the line that says so. */
function Between({ s, now }: { s: TournamentState; now: number }) {
  if (!s.config.autoAdvance || gamesDone(s)) return <div className="proj-idle">{nextRoundLine(s)}</div>;
  return <Countdown s={s} now={now} />;
}

function Countdown({ s, now }: { s: TournamentState; now: number }) {
  const { phaseEndsAt, pausedAt } = s.clock;
  if (phaseEndsAt === null) return null;
  const left = phaseEndsAt - (pausedAt ?? now);
  // the last ten seconds: a new element each second, so the clock jumps on every one
  const secs = Math.ceil(left / 1000);
  if (secs >= 1 && secs <= FINAL_SECONDS && pausedAt === null) {
    return (
      <div key={secs} className="proj-count wall-display tick">
        {clockText(left)}
      </div>
    );
  }
  return <div className="proj-count wall-display">{clockText(left)}</div>;
}

type CurseNow = Curse & { leftMs: number };

/**
 * The band between the header and the court blocks while the whole hall is under a twist: its
 * name and the seconds left. With no twist it says "Back to normal", for a few seconds after one ends.
 */
function TwistBand({ twist, paused }: { twist: TwistNow | null; paused: boolean }) {
  if (!twist) {
    return (
      <div className="proj-twist over">
        <span className="twist-name wall-display">Back to normal</span>
      </div>
    );
  }
  // the last ten seconds jump, like the clock
  const secs = Math.max(0, Math.ceil(twist.leftMs / 1000));
  const tick = secs >= 1 && secs <= FINAL_SECONDS && !paused;
  return (
    <div className="proj-twist">
      <span className="twist-label">Twist</span>
      <FitText className="twist-name wall-display">{twist.name}</FitText>
      <div className="twist-left">
        <b key={tick ? secs : 0} className={tick ? "wall-display tick" : "wall-display"}>
          {secs}
        </b>
        <span>{secs === 1 ? "second" : "seconds"}</span>
      </div>
    </div>
  );
}

function NowPlaying({
  s,
  now,
  host,
  twist,
  twistOver,
  curses,
}: {
  s: TournamentState;
  now: number;
  host: ReactNode;
  twist: TwistNow | null;
  twistOver: number | null;
  curses: CurseNow[];
}) {
  const round = currentRound(s);
  if (!round) return null;
  const timeUp = s.clock.phaseEndsAt !== null && (s.clock.pausedAt ?? now) >= s.clock.phaseEndsAt;
  const picks = pickTally(s, round.no);
  return (
    <>
      <Top title={`Round ${round.no}`} sub="Now playing" plan={planText(s, now)} middle={<Countdown s={s} now={now} />} host={host} />
      <div className="proj-play">
        {twist || twistOver !== null ? <TwistBand key={twist ? "on" : twistOver} twist={twist} paused={s.pausedAll} /> : null}
        <main key={round.no} className="proj-courts" style={{ "--n": round.games.length } as CSSProperties}>
          {round.games.map((g) => (
            <CourtCard
              key={g.court}
              s={s}
              g={g}
              timeUp={timeUp}
              bounty={bountiesOn(round, g.court)}
              curse={curses.find((c) => c.court === g.court) ?? null}
              picks={picks.get(g.court) ?? null}
            />
          ))}
        </main>
      </div>
    </>
  );
}

/** The price on a pair's heads: what the other pair each win by beating them. */
function Bounty({ value }: { value: number }) {
  return (
    <Burst className="bounty">
      <span>Bounty</span>
      <b>{value}</b>
    </Burst>
  );
}

/** Under a cursed pair's two names: what they are doing and for how much longer. */
function CurseTag({ s, curse }: { s: TournamentState; curse: CurseNow }) {
  const stays = curse.stepsOff ? s.rounds[curse.roundNo - 1]?.games.find((g) => g.court === curse.court)?.[curse.side].find((id) => id !== curse.stepsOff) : undefined;
  return (
    <div className="curse-tag">
      <FitText className="curse-what" min={0.6}>{stays ? `Solo: ${nameOf(s, stays)} alone` : curseName(curse)}</FitText>
      <b className="wall-display">{clockText(curse.leftMs)}</b>
    </div>
  );
}

function CourtCard({
  s,
  g,
  timeUp,
  bounty,
  curse,
  picks,
}: {
  s: TournamentState;
  g: GameRecord;
  timeUp: boolean;
  bounty: { a: number; b: number };
  curse: CurseNow | null;
  picks: { a: number; b: number } | null;
}) {
  const event = eventById(s.config, g.eventId);
  const pairClass = (side: "a" | "b"): string => `card-pair${bounty[side] ? " has-bounty" : ""}${curse?.side === side ? " cursed" : ""}`;
  let score = null;
  if (g.voided) score = <div className="card-note wall-display">void</div>;
  else if (g.score) score = <div className="card-score wall-display">{g.score.a}-{g.score.b}</div>;
  else if (timeUp) score = <div className="card-note wall-display">score?</div>;
  return (
    <section className={curse ? "card has-curse" : "card"} data-court={colourNo(g.court)} style={courtColour(g.court)}>
      {event ? <div className="card-tag">{event.name}</div> : null}
      <div className="card-head">
        <div className="card-no wall-display">
          <span>Court</span>
          <b>{g.court}</b>
        </div>
        {score}
      </div>
      <div className={pairClass("a")}>
        <Who s={s} id={g.a[0]} />
        <Who s={s} id={g.a[1]} />
        {bounty.a ? <Bounty value={bounty.a} /> : null}
        {curse?.side === "a" ? <CurseTag s={s} curse={curse} /> : null}
      </div>
      <div className="card-v">
        <span>v</span>
        {picks ? (
          <span className="card-picks">
            Picks{" "}
            <b className="wall-display">
              {picks.a} - {picks.b}
            </b>
          </span>
        ) : null}
      </div>
      <div className={pairClass("b")}>
        <Who s={s} id={g.b[0]} />
        <Who s={s} id={g.b[1]} />
        {bounty.b ? <Bounty value={bounty.b} /> : null}
        {curse?.side === "b" ? <CurseTag s={s} curse={curse} /> : null}
      </div>
    </section>
  );
}

/** The board swaps page every 20 s (Abhi, 5 Oct): ranks 1 to 24, then the rest with the fun board. */
const PAGE_MS = 20_000;
const BOARD_COLUMNS = 3;

function chunk<T>(rows: T[], maxColumns: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < rows.length && out.length < maxColumns; i += ROWS_PER_COLUMN) {
    out.push(rows.slice(i, i + ROWS_PER_COLUMN));
  }
  return out;
}

interface BoardPage {
  main: MainRow[][];
  fun: FunRow[][];
  label: string;
}

function boardPages(main: MainRow[], fun: FunRow[]): BoardPage[] {
  const funRows = fun.filter((r) => r.active);
  const first = main.slice(0, BOARD_COLUMNS * ROWS_PER_COLUMN);
  const rest = main.slice(first.length);
  const pages: BoardPage[] = [{ main: chunk(first, BOARD_COLUMNS), fun: [], label: rest.length ? `ranks 1 to ${first.length}` : "" }];
  if (rest.length || funRows.length) {
    // the rest of the board takes as many columns as it needs (33 players leave 9, so two), the
    // fun board gets what is left
    const mainCols = chunk(rest, BOARD_COLUMNS);
    const funCols = chunk(funRows, Math.max(0, BOARD_COLUMNS - mainCols.length));
    const bits = [rest.length ? `ranks ${first.length + 1} to ${main.length}` : "", funRows.length ? "the fun board" : ""].filter(Boolean);
    pages.push({ main: mainCols, fun: funCols, label: bits.join(" and ") });
  }
  return pages;
}

/** How long the board stands as it was before the last round, before the rows move to where they are now. */
const BOARD_BEFORE_MS = 1400;
const BOARD_SLIDE_MS = 900;

/**
 * Rows that change place slide there. Every element with data-flip under `ref` is remembered by
 * that id; when `order` changes, each one that moved starts from where it was. Places are read
 * from the layout (offsets), not from the screen, so a row that is still arriving is not misread.
 */
function useFlip(ref: RefObject<HTMLElement | null>, order: string): void {
  const was = useRef(new Map<string, { x: number; y: number }>());
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const next = new Map<string, { x: number; y: number }>();
    root.querySelectorAll<HTMLElement>("[data-flip]").forEach((el) => {
      const id = el.dataset.flip as string;
      const at = { x: el.offsetLeft, y: el.offsetTop };
      next.set(id, at);
      const old = was.current.get(id);
      if (!old || (Math.abs(old.x - at.x) < 1 && Math.abs(old.y - at.y) < 1)) return;
      el.animate([{ transform: `translate(${old.x - at.x}px, ${old.y - at.y}px)` }, { transform: "none" }], {
        duration: BOARD_SLIDE_MS,
        easing: "cubic-bezier(0.2, 0.9, 0.3, 1)",
      });
    });
    was.current = next;
  }, [ref, order]);
}

function Leaderboard({ s, now, host }: { s: TournamentState; now: number; host: ReactNode }) {
  // The board comes up as it stood before the last round, then the rows slide to where that
  // round put them: who climbed and who dropped is seen, not worked out.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSettled(true), BOARD_BEFORE_MS);
    return () => clearTimeout(id);
  }, []);
  const main = mainBoard(settled || !s.rounds.length ? s : { ...s, rounds: s.rounds.slice(0, -1) });
  const pages = boardPages(main, funBoard(s));
  const boardRef = useRef<HTMLElement>(null);
  useFlip(boardRef, main.map((r) => r.id).join(" "));
  const pageNo = pages.length > 1 ? Math.floor(now / PAGE_MS) % pages.length : 0;
  const page = pages[pageNo] as BoardPage;
  const cols = Math.max(1, page.main.length + page.fun.length);
  const last = s.rounds.length;
  const changeover = s.clock.phase === "changeover";
  // between rounds: why the next one has not started
  const wait = waitingFor(s, now);
  const idleLine = gamesDone(s)
    ? nextRoundLine(s)
    : wait?.why === "scores"
      ? `Round ${wait.roundNo + 1} starts when a score for round ${wait.roundNo} is in`
      : wait?.why === "soon"
        ? `Round ${wait.roundNo} starts in ${countdown(wait.startsAt - now)}`
        : nextRoundLine(s);
  const sub = (last ? `After round ${last}` : "Before round 1") + (pages.length > 1 ? `, ${page.label} (${pageNo + 1} of ${pages.length})` : "");
  return (
    <>
      <Top
        title="Leaderboard"
        sub={sub}
        plan={planText(s, now)}
        middle={changeover ? <Between s={s} now={now} /> : <div className="proj-idle">{idleLine}</div>}
        host={host}
      />
      <main ref={boardRef} className="proj-board" style={{ "--cols": cols } as CSSProperties}>
        {page.main.map((col, i) => (
          <ol key={`m${i}`} className="board-col">
            <li className="board-head">
              <span>#</span>
              <span>Name</span>
              <span>Games</span>
              <span>Avg</span>
            </li>
            {col.map((r, j) => (
              <li key={r.id} data-flip={r.id} className={r.eligible ? "board-row" : "board-row ineligible"} style={{ "--r": i * ROWS_PER_COLUMN + j } as CSSProperties}>
                {/* everyone is level before a game: no rank until the first one, as on the phone's Now tile */}
                <span className="board-rank">{r.games ? r.rank : "-"}</span>
                <FitText className="board-name">{nameOf(s, r.id)}</FitText>
                <span className="board-games">{r.games}</span>
                <span className="board-avg">{r.avg.toFixed(1)}</span>
              </li>
            ))}
          </ol>
        ))}
        {page.fun.map((col, i) => (
          <ol key={`f${i}`} className="board-col board-fun">
            <li className="board-head">
              <span>#</span>
              <span>Fun board</span>
              <span />
              <span>Pts</span>
            </li>
            {col.map((r, j) => (
              <li key={r.id} className="board-row" style={{ "--r": (page.main.length + i) * ROWS_PER_COLUMN + j } as CSSProperties}>
                <span className="board-rank">{r.rank}</span>
                <FitText className="board-name">{nameOf(s, r.id)}</FitText>
                <span />
                <span className="board-avg">{Math.round(r.funPoints)}</span>
              </li>
            ))}
          </ol>
        ))}
      </main>
    </>
  );
}

/** The end of a round: each court's winners on top, then the score, then the losers. */
function Results({ s, now, host, round, review }: { s: TournamentState; now: number; host: ReactNode; round: Round; review: Review | null }) {
  // `paid` is a bounty the winners each collected by beating the pair that carried it
  const pair = (p: readonly [PlayerId, PlayerId], className: string, paid = 0) => (
    <div className={`card-pair ${className}${paid ? " has-bounty" : ""}`}>
      <Who s={s} id={p[0]} />
      <Who s={s} id={p[1]} />
      {paid ? (
        <Burst className="bounty paid">
          <span>Bounty</span>
          <b>+{paid}</b>
          <span>each</span>
        </Burst>
      ) : null}
    </div>
  );
  // a run of right picks takes the plan's line for these six seconds
  const picks = picksSettled(s, round.no);
  return (
    <>
      <Top title={`Round ${round.no} results`} sub={picks.right} plan={picks.run ?? planText(s, now)} middle={<Between s={s} now={now} />} host={host} />
      <main className="proj-courts proj-results" style={{ "--n": round.games.length } as CSSProperties}>
        {round.games.map((g) => {
          const aWon = !!g.score && g.score.a > g.score.b;
          const bounty = bountiesOn(round, g.court);
          const stamp = review?.court === g.court ? review.stage : null;
          return (
            <section key={g.court} className="card result" data-court={colourNo(g.court)} style={courtColour(g.court)}>
              <div className="card-head">
                <div className="card-no wall-display">
                  <span>Court</span>
                  <b>{g.court}</b>
                </div>
                {g.score && !g.voided ? null : <div className="card-note wall-display">{g.voided ? "void" : "score?"}</div>}
              </div>
              {g.score && !g.voided ? (
                <>
                  {pair(aWon ? g.a : g.b, "result-win", aWon ? bounty.b : bounty.a)}
                  <div className="result-score wall-display">
                    {aWon ? g.score.a : g.score.b}-{aWon ? g.score.b : g.score.a}
                    {stamp ? (
                      <Burst key={stamp} className={`review ${stamp}`}>
                        {stamp === "checking" ? "Checking" : "Stands"}
                      </Burst>
                    ) : null}
                  </div>
                  {pair(aWon ? g.b : g.a, "result-lose")}
                </>
              ) : (
                <>
                  {pair(g.a, "result-open")}
                  <div className="card-v">v</div>
                  {pair(g.b, "result-open")}
                </>
              )}
            </section>
          );
        })}
      </main>
    </>
  );
}

/**
 * Courts still owing a score, grouped by round, oldest first: earlier rounds, and the round just
 * played once its clock has stopped (between rounds the wall said nothing until the final-day
 * run of 18 Oct; the admin's Courts ready line did, and the hall should hear it too).
 */
function lateScores(s: TournamentState): string[] {
  const current = currentRound(s)?.no ?? 0;
  const playing = s.clock.phase === "playing";
  const byRound = new Map<number, number[]>();
  for (const o of owedScores(s)) {
    if (o.roundNo < current || !playing) byRound.set(o.roundNo, [...(byRound.get(o.roundNo) ?? []), o.court]);
  }
  return [...byRound].map(([no, courts]) =>
    courts.length === 1 ? `Court ${courts[0]}: round ${no}'s score is still needed` : `${courtsText(courts, 0)}: round ${no}'s scores are still needed`,
  );
}

function UpNext({ s, late, link }: { s: TournamentState; late: string[]; link: string | null }) {
  const round = currentRound(s);
  const draft = s.draft;
  const waitingForScores = s.mode === "mexicano" && !!round && !allScoresIn(round);
  // nothing is drawn with fewer than a court's worth here (the rest away or on a break): say so
  const tooFew = availablePlayers(s).length < s.config.playersPerCourt;
  const sittingOut = draft ? draft.sittingOut : s.clock.phase === "playing" && round ? round.lineup.sittingOut : [];
  const line = (c: CourtGame) => {
    const event = eventById(s.config, c.eventId);
    return (
      <li key={c.court}>
        <span className="next-no" style={courtColour(c.court)}>
          {c.court}
        </span>
        <FitText className="next-names">{`${nameOf(s, c.a[0])} & ${nameOf(s, c.a[1])} v ${nameOf(s, c.b[0])} & ${nameOf(s, c.b[1])}`}</FitText>
        {event ? <span className="next-tag">{event.name}</span> : null}
      </li>
    );
  };
  return (
    <footer className="proj-strip">
      {late.length ? (
        <p className="strip-nag">
          <FitText className="wall-display">{late.join(" · ")}</FitText>
        </p>
      ) : null}
      <div className="strip-next">
        <h2>Up next</h2>
        {draft ? (
          <ol>{draft.courts.map(line)}</ol>
        ) : (
          <p className="strip-wait">
            {gamesDone(s)
              ? "No more games"
              : tooFew
                ? "Not enough people here yet"
                : waitingForScores
                  ? "Drawn when the scores are in"
                  : "Not drawn yet"}
          </p>
        )}
      </div>
      <div className="strip-out">
        <h2>{draft ? "Sitting out next" : "Sitting out"}</h2>
        <FitLines>{sittingOut.length ? sittingOut.map((id) => nameOf(s, id)).join(", ") : "Nobody"}</FitLines>
      </div>
      {link ? (
        <div className="proj-qr">
          <Qr link={link} className="proj-qr-code" />
          <FitText>Scores on your phone</FitText>
        </div>
      ) : null}
    </footer>
  );
}

/** A mini game or a twist, announced by the host: his name on the label, and him at the card's corner (decision 45). */
function EventCard({ a }: { a: Announcement }) {
  return (
    <div className="proj-overlay proj-event-wrap">
      <div className={a.items.length > 2 ? "proj-event many" : "proj-event"}>
        <p className="event-label">{`${a.label ?? "This round"}, by order of ${MASCOT.name}`}</p>
        {a.items.map((item) => (
          <div key={item.name} className="event-item">
            <h2>
              <FitText className="wall-display">{item.name}</FitText>
            </h2>
            <p className="event-courts">
              {item.courts.map((c) => (
                <span key={c} className="next-no" style={courtColour(c)}>
                  {c}
                </span>
              ))}
              <span>{item.note ?? courtsText(item.courts, a.total)}</span>
            </p>
          </div>
        ))}
        <Mascot pose="calling" className="event-host" />
      </div>
    </div>
  );
}

/**
 * The pause screen. A plain pause says "Paused" and what is left on the clock. A break with a
 * time (lunch) says what it is, when to be back and counts down; once that time has passed it
 * says so, and nothing starts until an admin resumes. A message sent during the pause is a line
 * of this screen until the pause ends; the host makes room for it.
 */
function Paused({ s, seed, now, message }: { s: TournamentState; seed: number; now: number; message: string | null }) {
  const { phaseEndsAt, pausedAt } = s.clock;
  const on = breakNow(s, now);
  // A break with a time (lunch) also says where to be when it ends: the round being played, if it
  // stopped mid-game, or else the next one drawn. Nothing when neither is known (Mexicano waiting).
  const playing = s.clock.phase === "playing" ? currentRound(s) : null;
  const courtsOf = (games: { court: number; a: Pair; b: Pair }[]) => games.map(({ court, a, b }) => ({ court, a, b }));
  const back = !on
    ? null
    : playing
      ? { label: `Back to round ${playing.no}`, courts: courtsOf(playing.games.filter((g) => !g.score && !g.voided)) }
      : s.draft
        ? { label: `First round back: round ${s.rounds.length + 1}`, courts: courtsOf(s.draft.courts) }
        : null;
  return (
    <div className={"proj-overlay proj-paused" + (on ? " has-break" : "") + (message ? " has-message" : "") + (back ? " has-next" : "")}>
      {on ? (
        <h1>
          <FitText className="wall-display">{on.label}</FitText>
        </h1>
      ) : (
        <h1 className="wall-display">Paused</h1>
      )}
      {on?.over ? <p className="paused-back wall-display">{BREAK_OVER}</p> : null}
      {on && !on.over && on.backAt ? (
        <>
          <p className="paused-back wall-display">{on.backAt}</p>
          {/* time left, not a time of day: "29:58 to go" under "Back at 13:35" */}
          <div className="paused-count wall-display">
            {countdown(on.leftMs ?? 0)} <span className="paused-to-go">to go</span>
          </div>
        </>
      ) : null}
      {!on && phaseEndsAt !== null && pausedAt !== null ? <p>{clockText(phaseEndsAt - pausedAt)} left on the clock</p> : null}
      {back && back.courts.length ? (
        <div className="paused-next">
          <h2>{back.label}</h2>
          <ol>
            {back.courts.map((c) => (
              <li key={c.court}>
                <span className="next-no" style={courtColour(c.court)}>
                  {c.court}
                </span>
                <FitText className="next-names">{`${nameOf(s, c.a[0])} & ${nameOf(s, c.a[1])} v ${nameOf(s, c.b[0])} & ${nameOf(s, c.b[1])}`}</FitText>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
      {/* the courts take the host's room on a lunch screen */}
      {message ? <Notice key={message} text={message} inline /> : back && back.courts.length ? null : <Host moment="pause" seed={seed} />}
    </div>
  );
}

const ORDINAL = ["", "1st", "2nd", "3rd"];

/** Squares of the court colours and the accent, falling for as long as the winner is up. */
const CONFETTI = Array.from({ length: 24 }, (_, i) => ({
  "--x": `${(i * 37 + 11) % 100}vw`,
  "--w": `${1.6 + (i % 4) * 0.6}vh`,
  "--c": i % 5 === 4 ? "var(--bd-accent)" : `var(--bd-court-${(i % 4) + 1})`,
  "--t": `${2.6 + (i % 5) * 0.45}s`,
  "--wait": `${((i * 7) % 12) * 0.25}s`,
  "--spin": `${i % 2 ? 540 : -420}deg`,
})) as CSSProperties[];

function Confetti() {
  return (
    <div className="confetti" aria-hidden="true">
      {CONFETTI.map((style, i) => (
        <i key={i} style={style} />
      ))}
    </div>
  );
}

/** One insight as a full-screen card, before the podium. */
function InsightCard({ item, seed }: { item: Insight; seed: number }) {
  return (
    <div className="proj-overlay proj-insight">
      <div className="insight-card">
        <h1>
          <FitText className="wall-display">{item.title}</FitText>
        </h1>
        <p className="insight-detail">{item.detail}</p>
        <Host moment="reel" seed={seed} />
      </div>
    </div>
  );
}

function Reveal({ s, now, seed, stepped, fresh }: { s: TournamentState; now: number; seed: number; stepped: RevealStep | null; fresh: boolean }) {
  const faceOf = useContext(Faces);
  const startedAt = useRef(now).current;
  // The insights reel runs first (main reveal only), four seconds a card, then the podium; the
  // owner's stepped reveal has no reel (his steps: the awards, then 3rd, 2nd, 1st).
  const reel = useRef(s.reveal.main && !stepped ? insights(s) : []).current;
  // stepped: the podium as many places as the owner's card counted when the reveal started
  const main = !!stepped || s.reveal.main;
  const elapsed = now - startedAt;
  const reelMs = reel.length * INSIGHT_MS;
  const top = main ? podium(mainBoard(s)).slice(0, stepped ? Math.max(0, Math.min(3, stepped.places)) : 3) : [];
  // Lowest place first, so the winner comes last (PLACE_AT_MS); laid out 2nd, 1st, 3rd.
  const since = elapsed - reelMs;
  const times = PLACE_AT_MS.slice(0, top.length);
  // Stepped: the owner's step says how many places; a fresh step rolls the drum, then shows it.
  const target = stepped ? Math.max(0, Math.min(top.length, stepped.step - stepped.of)) : 0;
  const [stepShown, setStepShown] = useState(() => (fresh ? Math.max(0, target - 1) : target));
  const stepShownRef = useRef(stepShown);
  stepShownRef.current = stepShown;
  useEffect(() => {
    if (!stepped) return;
    if (target <= stepShownRef.current) {
      if (target < stepShownRef.current) hush(); // Back: at once, and the anthem stops
      setStepShown(target);
      return;
    }
    if (!fresh) {
      setStepShown(target); // an old step (a reload, an undo): where it is, silently
      return;
    }
    const place = top.length - target + 1;
    const roll = STEP_ROLL_MS[place] ?? 2000;
    drumRoll(roll / 1000);
    const id = setTimeout(() => {
      setStepShown(target);
      if (target === top.length) anthem();
      else sting();
    }, roll);
    return () => clearTimeout(id);
  }, [target, !!stepped]);
  // taken off the wall, or Back into the awards: the anthem stops with it
  useEffect(() => () => hush(), []);
  const shownCount = stepped ? stepShown : since < 0 ? 0 : times.filter((at) => since >= at).length;
  // The sound: a chord for each place, a drum roll into the next, the fanfare for the winner.
  // Every render looks at the clock; the refs make each sound play once.
  const sounded = useRef(0);
  const rolled = useRef(0);
  useEffect(() => {
    if (stepped || since < 0 || !top.length) return;
    const roll = ROLL_MS[shownCount] ?? 0;
    const at = times[shownCount];
    if (at !== undefined && roll > 0 && rolled.current <= shownCount && since >= at - roll) {
      rolled.current = shownCount + 1;
      drumRoll(roll / 1000);
    }
    if (shownCount > sounded.current) {
      sounded.current = shownCount;
      if (shownCount === top.length) fanfare();
      else sting();
    }
  });
  if (elapsed < reelMs) {
    const i = Math.floor(elapsed / INSIGHT_MS);
    return <InsightCard key={i} item={reel[i] as Insight} seed={seed} />;
  }
  const funOn = !stepped && s.reveal.fun;
  const fun = funOn ? funBoard(s).slice(0, 3) : [];
  const shownFrom = top.length - shownCount; // index into top of the best place shown so far
  return (
    <div className="proj-overlay proj-reveal">
      {!main || (top.length > 0 && shownFrom === 0) ? <Confetti /> : null}
      <header className="reveal-top">
        <h1 className="wall-display">{main ? "The podium" : "Fun board"}</h1>
        <Host moment="podium" seed={seed} />
      </header>
      {main ? (
        top.length ? (
          <div className="podium">
            {[1, 0, 2].map((i) => {
              const row = top[i];
              if (!row) return <div key={i} className={`step step-${i + 1}`} />;
              const name = nameOf(s, row.id);
              // under a nickname, the real name, so the hall knows who it is (decision 43)
              const real = s.players[row.id]?.name;
              return (
                <div key={i} className={`step step-${i + 1}${i >= shownFrom ? " shown" : ""}`}>
                  <Face name={name} src={faceOf(s.players[row.id])} className="on-ground" stamped={lostLast(s, row.id)} crowned={isFavourite(row.id)} />
                  <FitText className="step-name wall-display">{name}</FitText>
                  {real && real !== name ? <FitText className="step-real">{real}</FitText> : null}
                  <div className="step-block">
                    <span className="step-place wall-display">{ORDINAL[i + 1]}</span>
                    <span className="step-avg">{row.avg.toFixed(1)} a game</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="reveal-none">Nobody has played enough games yet</p>
        )
      ) : null}
      {funOn ? (
        <div className={main ? "reveal-fun" : "reveal-fun alone"}>
          {main ? <h2>Fun board</h2> : null}
          <ol>
            {fun.map((r, i) => (
              <li key={r.id}>
                <span className="next-no" style={courtColour(i + 1)}>
                  {r.rank}
                </span>
                <span className="wall-display">{nameOf(s, r.id)}</span>
                <em>{Math.round(r.funPoints)} {Math.round(r.funPoints) === 1 ? "pt" : "pts"}</em>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

/** The winners' box on an award card is about this much wider than it is tall. */
const AWARD_BOX = 3.3;

/** An award's line without the names it opens with, when their tiles are already saying them. */
function awardFact(award: Award): string {
  const at = award.detail.lastIndexOf(": ");
  if (award.winners.length < 3 || at < 0) return award.detail;
  const fact = award.detail.slice(at + 2);
  return fact[0]!.toUpperCase() + fact.slice(1);
}

/** The awards, handed out one at a time before the podium: six seconds each, then round again. */
function Awards({ s, shown, at, of, fresh }: { s: TournamentState; shown?: RefObject<WallView["award"]>; at?: number | undefined; of?: number | undefined; fresh?: boolean }) {
  const faceOf = useContext(Faces);
  const startedAt = useRef(Date.now()).current;
  // stepped: the awards the owner's card counted when the reveal started, in the same order
  const all = awards(s, "wall");
  const list = of === undefined ? all : all.slice(0, Math.max(0, of));
  // the owner's stepped reveal (decision 53): the award his step is on, with a flourish when fresh
  const stepAt = at === undefined ? null : Math.max(0, Math.min(list.length - 1, at));
  useEffect(() => {
    if (stepAt !== null && fresh && list.length) flourish();
  }, [stepAt]);
  if (!list.length) {
    return (
      <div className="proj-overlay proj-awards">
        <p className="reveal-none">No awards yet</p>
      </div>
    );
  }
  const i = stepAt ?? Math.floor((Date.now() - startedAt) / AWARD_MS) % list.length;
  const award = list[i] as Award;
  if (shown) shown.current = { n: i + 1, of: list.length, title: award.title };
  const n = award.winners.length;
  const cols = columnsFor(n, AWARD_BOX);
  const rows = Math.ceil(n / cols);
  // a score stays on one line
  const fact = awardFact(award).split(/(\d+-\d+)/);
  return (
    <div className="proj-overlay proj-awards">
      <div key={i} className="award">
        <header className="award-top">
          <div className="award-title">
            <p className="award-count">{`${MASCOT.name}'s award ${i + 1} of ${list.length}`}</p>
            <h1>
              <FitText className="wall-display">{award.title}</FitText>
            </h1>
          </div>
          {/* the host hands each one out (decision 45) */}
          <Mascot pose="smug" className="award-host" />
          <div className="award-points">
            <b className="wall-display">+{award.points}</b>
            <span>{n > 1 ? "fun points each" : "fun points"}</span>
          </div>
        </header>
        <div className="award-who">
          <div className="join-cells" style={{ "--cols": cols, "--rows": rows } as CSSProperties}>
            {award.winners.map((id, j) => {
              const name = nameOf(s, id);
              return (
                <div key={id} className="join-tile" data-court={(j % 4) + 1} style={{ "--i": j } as CSSProperties}>
                  <Face name={name} src={faceOf(s.players[id])} crowned={isFavourite(id)} />
                  <div className="join-plate">
                    <FitText className="join-name wall-display">{name}</FitText>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <p className="award-detail">{fact.map((bit, j) => (j % 2 ? <b key={j}>{bit}</b> : bit))}</p>
      </div>
    </div>
  );
}
